import React, { useCallback } from 'react';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import {
  CenterListingByTypeProps,
  CenterSummaryModel,
  CenterTypeColumnProps,
  ParticipatingHospitalModel,
} from 'lib/salesforce/types';
import SalesforceTable from './lib/SaleforceTable';
import GetCenterTypeColumnValue from './GetCenterTypeColumnValue';
import GetParticipatingHospitalColumnValue from './GetParticipatingHospitalColumnValue';
import { logWarning, makeLoggableError } from 'lib/util/errors';

const withSafeSorter = <T,>(
  sorter: ((a: T, b: T) => number) | undefined
): ((a: T, b: T) => number) => {
  return (a: T, b: T) => {
    if (sorter) {
      try {
        return sorter(a, b);
      } catch (e) {
        logWarning('SalesforceTable::sort', makeLoggableError({ error: e }));
        return 0;
      }
    }
    return 0;
  };
};

const getCenterTypeSorterForColumn = (
  name: string
): ((a: CenterSummaryModel, b: CenterSummaryModel) => number) | undefined => {
  const centerSorter = {
    byCountry: withSafeSorter((a: CenterSummaryModel, b: CenterSummaryModel) => {
      const countrySort = a?.Address?.Country.localeCompare(b?.Address?.Country);
      // If country is the same, sort by name
      return countrySort === 0 ? a.Name.localeCompare(b.Name) : countrySort;
    }),
    byName: withSafeSorter((a: CenterSummaryModel, b: CenterSummaryModel) =>
      a.Name.localeCompare(b.Name)
    ),
  };
  if (name === 'Country') {
    return centerSorter.byCountry;
  }
  if (name === 'Name') {
    return centerSorter.byName;
  }
  return undefined;
};

const getParticipatingHospitalSorterForColumn = (
  name: string
): ((a: ParticipatingHospitalModel, b: ParticipatingHospitalModel) => number) | undefined => {
  const centerSorter = {
    byHospital: withSafeSorter((a: ParticipatingHospitalModel, b: ParticipatingHospitalModel) =>
      a.HospitalName.localeCompare(b.HospitalName)
    ),
    byCity: withSafeSorter((a: ParticipatingHospitalModel, b: ParticipatingHospitalModel) =>
      a.City.localeCompare(b.City)
    ),
    byState: withSafeSorter((a: ParticipatingHospitalModel, b: ParticipatingHospitalModel) => {
      const countrySort = a?.State.localeCompare(b?.State);
      // If state is the same, sort by hospitalname
      return countrySort === 0 ? a.HospitalName.localeCompare(b.HospitalName) : countrySort;
    }),
  };
  if (name === 'HospitalName') {
    return centerSorter.byHospital;
  }
  if (name === 'City') {
    return centerSorter.byCity;
  }
  if (name === 'State') {
    return centerSorter.byState;
  }
  return undefined;
};
const getCenterTypeShouldSort = (name: string) => name === 'Name' || name === 'Country';
const getParticipatingHospitalShouldSort = (name: string) =>
  name === 'HospitalName' || name === 'City' || name === 'State';

const CenterTypeTable = SalesforceTable<CenterSummaryModel>;
const ParticipatingHospitalCenterTable = SalesforceTable<ParticipatingHospitalModel>;

const CenterListingByType = (props: CenterListingByTypeProps): React.JSX.Element => {
  const { fields } = props;
  const { centerTypeTableColumns: columnNamesProp, centerType } = fields ?? {
    centerTypeTableColumns: [],
  };

  const onCenterTypeDataLoaded = useCallback(
    (input: CenterSummaryModel[]) => {
      const columnNames = columnNamesProp as CenterTypeColumnProps[];
      const modelList = [...input];
      if (columnNames.find((item) => item.fields?.salesForceField?.name === 'Country')) {
        modelList.sort(getCenterTypeSorterForColumn('Country'));
      } else if (columnNames.find((item) => item.fields?.salesForceField?.name === 'Name')) {
        modelList.sort(getCenterTypeSorterForColumn('Name'));
      }
      return modelList;
    },
    [columnNamesProp]
  );
  const onParticipatingHospitalDataLoaded = useCallback(
    (input: ParticipatingHospitalModel[]) => {
      const columnNames = columnNamesProp as CenterTypeColumnProps[];
      const modelList = [...input];
      if (columnNames.find((item) => item.fields?.salesForceField?.name === 'State')) {
        modelList.sort(getParticipatingHospitalSorterForColumn('State'));
      }
      return modelList;
    },
    [columnNamesProp]
  );
  if (!fields || !centerType?.name) {
    return <MissingDataSource />;
  }

  return centerType.name === 'Participating Hospitals' ? (
    <ParticipatingHospitalCenterTable
      {...props}
      apiUrl={`${process.env.PUBLIC_URL}/api/sitecore/NmdpSalesforce/GetParticipatingHospitals`}
      getCenterSorterForColumn={getParticipatingHospitalSorterForColumn}
      getShouldSort={getParticipatingHospitalShouldSort}
      onDataLoaded={onParticipatingHospitalDataLoaded}
      GetColumnValue={GetParticipatingHospitalColumnValue}
      defaultSortColumn="State"
    />
  ) : (
    <CenterTypeTable
      {...props}
      apiUrl={`${process.env.PUBLIC_URL}/api/sitecore/NmdpSalesforce/GetCenterTypeByName`}
      getCenterSorterForColumn={getCenterTypeSorterForColumn}
      getShouldSort={getCenterTypeShouldSort}
      onDataLoaded={onCenterTypeDataLoaded}
      GetColumnValue={GetCenterTypeColumnValue}
      defaultSortColumn="Country"
    />
  );
};

export default CenterListingByType;
